import { questionTypeLabel } from '../../../../lib/votings';
import { __, declension, formatDateTime } from '../../../../utils';
import type { FormValues } from './formValues';
import {
  Box,
  Button,
  Dialog,
  Divider,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  open: boolean;
  values: FormValues;
  loading?: boolean;
  onCancel: () => void;
  /** „Doplniť dátum" - zavrie kontrolu a otvorí nastavenia. */
  onFixDates: () => void;
  onConfirm: () => void;
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Stack direction='row' alignItems='baseline' gap={1.5} sx={{ fontSize: 14 }}>
    <Box
      component='span'
      sx={{ width: 120, flex: 'none', color: 'text.secondary' }}
    >
      {label}
    </Box>
    <Box component='span' sx={{ flex: 1 }}>
      {children}
    </Box>
  </Stack>
);

/**
 * Kontrola pred publikovaním.
 *
 * Nahrádza tri varianty potvrdenia „chýba dátum otvorenia / zatvorenia /
 * oboje", ktoré sa pýtali na jednu vec a o zvyšku hlasovania mlčali. Publikovanie
 * je nevratné (od otvorenia sa hlasovanie nedá upraviť), takže tu má stáť
 * súhrn toho, čo sa publikuje - a chýbajúci dátum je v ňom **jeden riadok
 * s upozornením**, nie celá otázka.
 */
const PublishDialog: React.FC<Props> = ({
  open,
  values,
  loading,
  onCancel,
  onFixDates,
  onConfirm,
}) => {
  const types = Array.from(
    new Set(values.questions.map((q) => q.questionType)),
  ).map((type) => questionTypeLabel(type).toLocaleLowerCase('sk'));

  const audienceLabel = {
    anyone: __('ktokoľvek, aj neprihlásený'),
    authenticated: __('prihlásení používatelia'),
    verified: __('iba overené účty'),
  }[values.audience];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth>
      <Stack sx={{ p: 3, pb: 2, gap: 0.75 }}>
        <Typography
          component='b'
          sx={{ fontSize: 19, fontWeight: 700, lineHeight: 1.3 }}
        >
          {__('Publikovať hlasovanie?')}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {__('Od otvorenia sa už nedá upraviť.')}
        </Typography>
      </Stack>

      <Divider />

      <Stack sx={{ px: 3, py: 2, gap: 1.25 }}>
        <Row label={__('Otázky')}>
          {`${values.questions.length} ${declension(values.questions.length, [
            __('otázka'),
            __('otázky'),
            __('otázok'),
          ])}${types.length ? ` · ${types.join(', ')}` : ''}`}
        </Row>
        <Row label={__('Otvorenie')}>
          {values.openFrom ? (
            formatDateTime(values.openFrom)
          ) : (
            <Box component='span' sx={{ color: 'yellow.surfaceText' }}>
              {__('nezadané - otvorí sa hneď po publikovaní')}
            </Box>
          )}
        </Row>
        <Row label={__('Uzavretie')}>
          {values.openTo ? (
            formatDateTime(values.openTo)
          ) : (
            <Box component='span' sx={{ color: 'yellow.surfaceText' }}>
              {__('nezadané - treba zavrieť ručne')}
            </Box>
          )}
        </Row>
        <Row label={__('Hlasovať môžu')}>{audienceLabel}</Row>
        <Row label={__('Výsledky')}>
          {!values.availableResults
            ? __('občan ich neuvidí, vidieť ich budete len vy v správe')
            : values.availableIncompleteResults
              ? __('priebežné podrobné výsledky počas hlasovania')
              : __('počet hlasov počas priebehu, podrobnosti po uzavretí')}
        </Row>

        {!values.openTo && (
          <Stack
            direction='row'
            alignItems='flex-start'
            gap={1.25}
            sx={({ palette, shape }) => ({
              mt: 0.5,
              border: `1px solid ${palette.yellow.main}`,
              borderRadius: `${shape.borderRadius}px`,
              backgroundColor: palette.yellow.surface,
              px: 1.75,
              py: 1.5,
            })}
          >
            <Box
              sx={({ palette, shape }) => ({
                width: 20,
                height: 20,
                flex: 'none',
                display: 'grid',
                placeItems: 'center',
                borderRadius: `${shape.borderRadiusFull}px`,
                backgroundColor: palette.yellow.main,
                color: palette.grey[900],
                fontSize: 12,
                fontWeight: 700,
              })}
            >
              !
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: 1.5,
                color: 'yellow.surfaceText',
              }}
            >
              {__(
                'Bez dátumu uzavretia hlasovanie beží, kým ho niekto nezavrie. Odporúčame doplniť dátum.',
              )}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Divider />

      <Stack direction='row' alignItems='center' gap={1} sx={{ px: 3, py: 2 }}>
        <Button variant='text' onClick={onCancel} disabled={loading}>
          {__('Zrušiť')}
        </Button>
        <Button
          variant='outline'
          sx={{ ml: 'auto' }}
          onClick={onFixDates}
          disabled={loading}
        >
          {__('Doplniť dátum')}
        </Button>
        <Button onClick={onConfirm} loading={loading}>
          {__('Publikovať')}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default PublishDialog;
